import { computed, ref, toRef, type MaybeRef } from 'vue'

import type { ICustomerFilterParam } from '@/services'
import { Customer, type ICustomerData } from '@/models/customer'

import customerService from '@/services/customer/customer'

import { useSnackbarStore } from '@/stores/snackbar'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import userService from '@/services/user/user'
import { User } from '@/models/user'

export function useFetchCustomers(filter?: ICustomerFilterParam) {
  const queryKey = ref(['customers', filter])

  const count = ref<number>(0)

  const {
    isLoading,
    isError,
    error,
    data: customers
  } = useQuery({
    queryKey: queryKey,
    queryFn: () => customerService.fetchCustomers(filter),

    select: (data) => {
      count.value = data.count
      return data.results.map((customer) => new Customer(customer))
    }
  })

  return { queryKey, customers, count, error, isError, isLoading }
}

export function useFetchCustomer(id: MaybeRef<number>) {
  const customerIdRef = toRef(id)

  const queryKey = ref(['customer', customerIdRef])

  const {
    data: customer,
    isPending,
    error
  } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(customerIdRef.value)),
    queryFn: () => customerService.fetchCustomer(customerIdRef.value),
    select: (data) => new Customer(data)
  })

  return { customer, isPending, error, queryKey }
}

export function useCreateCustomer() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['create-customer'],
    mutationFn: (customer: ICustomerData) => customerService.createCustomer(customer),

    onSuccess: (data) => {
      queryClient.setQueryData(['customer', data.id], data)
      queryClient.invalidateQueries({ queryKey: ref(['customers']) })
      snackbarStore.showSnackbar('Customer created successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useUpdateCustomer() {
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-customer'],
    mutationFn: (customer: ICustomerData) => customerService.updateCustomer(customer),

    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ref(['customer', data.id]) })
      queryClient.invalidateQueries({ queryKey: ref(['customers']) })
      snackbarStore.showSnackbar('Customer updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useDeleteCustomer() {
  const mutationKey = ref(['delete-customer'])

  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (id: number) => customerService.deleteCustomer(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['customers']) })
      snackbarStore.showSnackbar('Customer deleted successfully', '')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useActiveStateCustomer(id: MaybeRef<number>) {
  const customerIdRef = toRef(id)

  const mutationKey = ref(['customer', customerIdRef.value])

  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    // Look to refactor when user composable has to be used
    mutationFn: (isActive: boolean) =>
      userService.setUserActiveState(customerIdRef.value, isActive),

    onSuccess: (data) => {
      const user = new User(data)
      queryClient.setQueryData(['customer', data.id], (oldCustomer: ICustomerData) =>
        oldCustomer
          ? {
              ...oldCustomer,
              is_active: data.is_active
            }
          : oldCustomer
      )

      snackbarStore.showSnackbar(
        `${user.getFullName()} has been ${user.is_active ? 'activated' : 'deactivated'}`,
        user.is_active ? 'success' : ''
      )
    },
    onSettled: async () => {
      return await queryClient.invalidateQueries({ queryKey: ['customers'] })
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useChangePasswordCustomer(id: MaybeRef<number>) {
  const customerIdRef = toRef(id)
  const mutationKey = ref(['customer', customerIdRef.value])

  const snackbarStore = useSnackbarStore()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (password: string) => userService.changeUserPassword(customerIdRef.value, password),
    onSuccess: () => {
      snackbarStore.showSnackbar('Password has been updated for user', 'success')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

export function useSetCustomerSites(id: MaybeRef<number>) {
  const idRef = toRef(id)
  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { data, isPending, isError, error, mutate, reset } = useMutation({
    mutationKey: ref(['customer', idRef.value]),
    mutationFn: (sites: number[]) => customerService.setCustomerSites(idRef.value, sites),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ref(['customer', data.id]) })
      snackbarStore.showSnackbar('Customer sites updated successfully')
    }
  })

  return {
    data,
    isPending,
    isError,
    error,
    mutate,
    reset
  }
}
